/* eslint-disable camelcase */
import axios from 'axios';
import { toast } from 'react-toastify';
import { showLoginModal } from 'controllers/login-modal-controller';
import {
  classifyRefreshFailure,
  getAccessToken,
  isAuthRefreshUrl,
  notifySessionInvalid,
  refreshSession,
} from './sessionRefresh';

let isShowingToast = false;

const noop = () => null;

function createInstance(apiUrl) {
  return axios.create({
    baseURL: apiUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

function handleResponse(response) {
  if (response.config.responseType === 'blob') {
    return response;
  }
  if (response.config.returnFullResponse) {
    return response;
  }
  return response.data;
}

function showExpiredToastOnce() {
  if (isShowingToast) return;
  isShowingToast = true;
  toast.error('Sua sessão expirou. Por favor, faça login novamente.');
  setTimeout(() => {
    isShowingToast = false;
  }, 3000);
}

export class Config {
  constructor({
    api_url = 'https://api.pro.agroger.com.br',
    has_user_header = false,
  }) {
    if (!api_url) throw new Error('Missing api_url');
    this.apiUrl = api_url;
    this.axios = createInstance(api_url, has_user_header);

    this.axios.interceptors.request.use((request) => {
      const token = getAccessToken();
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    });

    this.axios.interceptors.response.use(
      (response) => handleResponse(response),
      (error) => this.handleError(error),
    );
  }

  async handleError(error) {
    const status = error.response?.status;
    const originalRequest = error.config || {};
    const requestUrl = originalRequest.url || '';
    const method = originalRequest.method || 'get';

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (error.response?.data?.type === 'financeiro') {
      notifySessionInvalid({ financial: true, error });
      return Promise.reject(error);
    }

    if (isAuthRefreshUrl(requestUrl, method) || originalRequest._retry) {
      return Promise.reject(error);
    }

    try {
      const tokens = await refreshSession(this.apiUrl);
      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;

      const retryResponse = await this.axios.request(originalRequest);
      return retryResponse;
    } catch (refreshError) {
      const kind = classifyRefreshFailure(refreshError);

      if (kind === 'network' || kind === 'server') {
        return Promise.reject(refreshError);
      }

      const financial = kind === 'financial';
      notifySessionInvalid({
        financial,
        error: refreshError,
        showLoginModal: !financial,
      });

      if (!financial) {
        showExpiredToastOnce();
        showLoginModal();
      }

      return Promise.reject(refreshError);
    }
  }

  intercept(hook, successFn = noop, errFn = () => {}) {
    const allowedHooks = new Set(['request', 'response']);
    if (!allowedHooks.has(hook)) {
      throw new Error(
        'Just request and response are allowed to be intercepted',
      );
    }

    this.axios.interceptors[hook].use(
      async (request) => {
        const data = successFn(request);
        if (hook === 'response' && !!data) {
          return successFn(request);
        }
        return request;
      },
      (error) => {
        errFn(error);
        return Promise.reject(error);
      },
    );
  }

  instance() {
    return this.axios;
  }
}

export default (config) => new Config(config);
